/**
 * @file    enterprisePlugin.tsx
 * @scope   ENTERPRISE
 * @summary Enterprise V2 registration in the drawer engine host.
 * @consumes EnterpriseContext, EnterpriseRoutes, useEnterpriseTabCleanup, trend restorer
 * @produces enterprisePlugin
 * @sideEffects Provider mirrors the Enterprise slice into the global store.
 */
import React, { useCallback } from 'react';
import { useStore } from 'store/useGlobalStore';
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import {
  EnterpriseProvider,
  useEnterpriseContext,
} from 'components/DiyWidgetEngine/Subtypes/Enterprise/State/EnterpriseContext';
import { restoreSelectedChartTrend } from 'components/DiyWidgetEngine/Subtypes/Enterprise/utils/mapping/trendMapping';
import EnterpriseRoutes from '../../sections/EnterpriseRoutes';
import { useEnterpriseTabCleanup } from '../../hooks/useEnterpriseTabCleanup';
import { EnterpriseNavigationProvider } from './EnterpriseNavigationContext';
import { EngineSubtypePlugin } from '../types';

const EnterprisePluginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const setState = useStore((state) => state.setState);

  // WHY: the renderer/preview live outside this provider and read the selection from the store.
  const handleStateChange = useCallback(
    (enterpriseGlobalObj: EnterpriseGlobalModel) => setState({ enterpriseGlobalObj }),
    [setState]
  );

  return (
    <EnterpriseProvider onStateChange={handleStateChange}>
      <EnterpriseNavigationProvider>{children}</EnterpriseNavigationProvider>
    </EnterpriseProvider>
  );
};

const useEnterpriseResetSelection = () => useEnterpriseContext().clearSelectedValues;

export const enterprisePlugin: EngineSubtypePlugin = {
  id: 'enterprise',
  Provider: EnterprisePluginProvider,
  Routes: EnterpriseRoutes,
  useTabCleanup: useEnterpriseTabCleanup,
  useResetSelection: useEnterpriseResetSelection,
  restoreTrend: restoreSelectedChartTrend,
};
